import {
  AlertCircle,
  AlertTriangle,
  ArrowBigUpDash,
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle,
  Clock,
  Construction,
  Eye,
  EyeOff,
  Info,
  Loader2,
  Lock,
  Mail,
  Moon,
  Search,
  Smartphone,
  Sparkle,
  Sun,
  X,
  type LucideIcon,
} from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * Icon wrapper — Q093 hierarchical fallback.
 *
 * TD-02 fix: explicit icon map вместо `import * as LucideIcons` —
 * tree-shaking теперь работает (~663kB → ~30kB).
 *
 * Library priority:
 * 1. Lucide (default, встроена в shadcn) — большинство UI-иконок
 * 2. Phosphor (для CTA / EmptyState — 6 weights) — через `import { LockBold } from '@phosphor-icons/react'`
 * 3. Tabler (резерв для редких доменов: crypto, brand)
 * 4. Material Design Icons (только платёжные системы — Apple Pay / Mir / VISA)
 *
 * Standard sizes per Q093: 16 / 20 / 24 px
 *
 * @example
 *   <Icon name="check" size={20} className="text-text-success" />
 *   <Icon name="mail" size={24} className="text-accent-brand-primary" />
 *
 * Чтобы добавить новую иконку — импортируй её сверху и добавь в ICON_MAP.
 */

const ICON_MAP: Record<string, LucideIcon> = {
  'alert-circle': AlertCircle,
  'alert-triangle': AlertTriangle,
  'arrow-big-up-dash': ArrowBigUpDash,
  'arrow-left': ArrowLeft,
  'arrow-right': ArrowRight,
  check: Check,
  'check-circle': CheckCircle,
  clock: Clock,
  construction: Construction,
  eye: Eye,
  'eye-off': EyeOff,
  info: Info,
  'loader-2': Loader2,
  lock: Lock,
  mail: Mail,
  moon: Moon,
  search: Search,
  smartphone: Smartphone,
  sparkle: Sparkle,
  sun: Sun,
  x: X,
}

export type IconName = keyof typeof ICON_MAP

export interface IconProps extends Omit<React.SVGProps<SVGSVGElement>, 'name'> {
  name: IconName | string
  size?: 16 | 20 | 24 | number
}

export function Icon({ name, size = 20, className, ...props }: IconProps) {
  const Component = ICON_MAP[name]
  if (!Component) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn(`[Icon] "${name}" not in ICON_MAP. Add it to src/components/icons/Icon.tsx.`)
    }
    return <span className={cn('inline-block', className)} style={{ width: size, height: size }} />
  }
  return (
    <Component
      width={size}
      height={size}
      strokeWidth={2}
      className={cn('inline-block flex-shrink-0', className)}
      aria-hidden="true"
      {...props}
    />
  )
}
